import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { usePrompt } from './PromptDialog';
import { FiSettings, FiInfo, FiAlertTriangle, FiCheck, FiRefreshCw, FiSearch, FiPlus, FiTrash2 } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { menuTitle } from './workspace/WorkspaceDock';
import './SessionSettingsModal.css';

// 세션 설정 — 자동커밋 / 트랜잭션 격리수준 / client info / ALTER SESSION 파라미터.
//
// [자동커밋이 이 화면의 핵심이다]
// 기존 동작은 autoCommit=false 인데 커밋 수단이 없어서, 에디터에서 INSERT/UPDATE 를 해도
// 반영되지 않고 트랜잭션만 열린 채 남았다. 그래서 토글을 맨 위에 두고 상태를 분명히 보여준다.

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const SS_API = `${API_ROOT}/sql-developer/session-settings`;

// sessionId — 활성 편집기 탭의 세션. COMMIT/ROLLBACK 은 **그 세션의 커넥션**에 간다(풀 커넥션에 보내던 경로는 없앴다, 2026-10-03)
const SessionSettingsModal = ({ isOpen, onClose, connectionId, connectionName, sessionId }) => {
    const { t } = useTranslation();
    const prompt = usePrompt();
    const [catalog, setCatalog] = useState(null);
    const [settings, setSettings] = useState(null);
    const [live, setLive] = useState(null);
    const [liveError, setLiveError] = useState(null);
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        if (!connectionId) return;
        setBusy(true); setError(null);
        try {
            const [cat, cur] = await Promise.all([
                axios.get(`${SS_API}/catalog`, { params: { connectionId } }),
                axios.get(`${SS_API}/current`, { params: { connectionId } }),
            ]);
            setCatalog(cat.data);
            setSettings(cur.data.settings);
            setLive(cur.data.live);
            setLiveError(cur.data.liveError);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally { setBusy(false); }
    }, [connectionId]);

    useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

    const patch = async (body, msg) => {
        setBusy(true); setError(null); setNotice(null);
        try {
            const r = await axios.put(SS_API, { connectionId, ...body });
            setSettings(r.data.settings);
            if (msg) setNotice(msg);
        } catch (err) { setError(err.response?.data?.error || err.message); }
        finally { setBusy(false); }
    };

    const txn = async (kind) => {
        setBusy(true); setError(null); setNotice(null);
        try {
            await axios.post(`${API_ROOT}/sql-developer/sessions/${encodeURIComponent(sessionId)}/${kind}`);
            setNotice(t('common:sess.ranStatement', { kind: kind === 'commit' ? 'COMMIT' : 'ROLLBACK' }));
        } catch (err) { setError(err.response?.data?.error || err.message); }
        finally { setBusy(false); }
    };

    const params = useMemo(() => {
        const q = search.trim().toUpperCase();
        const all = [
            ...(catalog?.nls || []).map(p => ({ ...p, group: 'NLS' })),
            ...(catalog?.param || []).map(p => ({ ...p, group: 'PARAM' })),
        ];
        return q ? all.filter(p => p.name.toUpperCase().includes(q)) : all;
    }, [catalog, search]);

    const overrides = settings?.alterSession || {};

    const setOverride = (name, value) =>
        patch({ alterSession: { ...overrides, [name]: value } });

    const removeOverride = (name) => {
        const next = { ...overrides };
        delete next[name];
        patch({ alterSession: next }, t('common:sess.overrideCleared', { name: name }));
    };

    if (!isOpen) return null;

    // 작업 탭(2026-11-04 사용자 요청) — 제목은 메뉴 문구(탭을 앞으로 부르는 기준), 연결 이름은 제목 옆에. 절반 폭 · 새 창 분리 없음
    return (
        <AppModal windowed noPopout half open={!!isOpen} onClose={onClose} className="sscfg-modal"
            title={menuTitle(t('menu:connection.sessionSettings'))} icon={<FiSettings size={14} />}
            titleExtra={<span className="sscfg-conn">{connectionName || connectionId || t('common:sess.noConn')}</span>}>

                <div className="sscfg-body">
                    {!connectionId && <div className="sscfg-warn"><FiAlertTriangle size={13} /> {t('common:sess.pickConnFirst')}</div>}

                    {settings && (
                        <>
                            {/* 자동 커밋 — 이 화면에서 가장 중요한 항목 */}
                            <div className={`sscfg-autocommit ${settings.autoCommit ? 'on' : 'off'}`}>
                                <label className="sscfg-toggle">
                                    <input type="checkbox" checked={!!settings.autoCommit}
                                        onChange={e => patch({ autoCommit: e.target.checked })} disabled={busy} />
                                    <strong>{t('common:sess.autoCommit')}</strong>
                                    <span className="sscfg-state">{settings.autoCommit ? 'ON' : 'OFF'}</span>
                                </label>
                                <p>
                                    {settings.autoCommit
                                        ? t('common:sess.autoCommitHint')
                                        : t('common:sess.noAutoCommitHint')}
                                </p>
                                <div className="sscfg-txn">
                                    <button className="app-btn" onClick={() => txn('commit')} disabled={busy || !sessionId}>
                                        <FiCheck size={12} /> COMMIT
                                    </button>
                                    <button className="app-btn" onClick={() => txn('rollback')} disabled={busy || !sessionId}>
                                        ROLLBACK
                                    </button>
                                    <span className="sscfg-dim">
                                        {sessionId ? t('common:sess.sessionHint') : t('common:sess.noSessionHint')}
                                    </span>
                                </div>
                            </div>

                            {/* 격리 수준 */}
                            <div className="sscfg-field">
                                <span className="sscfg-label">{t('common:sess.isolation')}</span>
                                <select value={settings.isolationLevel || ''} disabled={busy}
                                    onChange={e => patch({ isolationLevel: e.target.value })}>
                                    <option value="">{t('common:sess.unchanged')}</option>
                                    <option value="READ COMMITTED">READ COMMITTED</option>
                                    <option value="SERIALIZABLE">SERIALIZABLE</option>
                                </select>
                            </div>

                            {/* client info 계열 */}
                            <div className="sscfg-section">{t('common:sess.appInfo')} <span className="sscfg-dim">{t('common:sess.appInfoHint')}</span></div>
                            {[
                                ['clientInfo', 'Client Info', 64],
                                ['module', 'Module', 48],
                                ['action', 'Action', 32],
                                ['identifier', 'Client Identifier', 64],
                            ].map(([key, label, max]) => (
                                <div className="sscfg-field" key={key}>
                                    <span className="sscfg-label">{label}</span>
                                    <input
                                        type="text" maxLength={max} disabled={busy}
                                        defaultValue={settings[key] || ''}
                                        placeholder={t('common:sess.unsetMax', { max: max })}
                                        onBlur={e => {
                                            if ((settings[key] || '') !== e.target.value) patch({ [key]: e.target.value });
                                        }}
                                    />
                                </div>
                            ))}

                            {/* 실제 세션 반영 상태 */}
                            <div className="sscfg-live">
                                <FiInfo size={12} />
                                {live
                                    ? <span>{t('common:sess.liveSid', { sid: live.sid })} · MODULE <code>{live.module || '-'}</code> · ACTION <code>{live.action || '-'}</code> · CLIENT_INFO <code>{live.clientInfo || '-'}</code> · ID <code>{live.clientIdentifier || '-'}</code></span>
                                    : <span>{t('common:sess.vsessionFailed', { message: liveError || t('common:sess.checkPrivilege') })}</span>}
                                <button className="sscfg-mini" onClick={load} title={t('common:sess.refresh')}><FiRefreshCw size={11} /></button>
                            </div>

                            {/* ALTER SESSION 파라미터 */}
                            <div className="sscfg-section">
                                {t('common:sess.params')}
                                <span className="sscfg-dim">
                                    {catalog?.paramError
                                        ? ` ${t('common:sess.noVParameter')}`
                                        : ` ${t('common:sess.catalogCount', { params: (catalog?.param || []).length, nls: (catalog?.nls || []).length })}`}
                                </span>
                            </div>

                            {Object.keys(overrides).length > 0 && (
                                <div className="sscfg-overrides">
                                    {Object.entries(overrides).map(([name, value]) => (
                                        <div className="sscfg-override" key={name}>
                                            <code>{name}</code> = <code>{value}</code>
                                            <button className="sscfg-mini" onClick={() => removeOverride(name)} title={t('common:sess.clear')}>
                                                <FiTrash2 size={11} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="sscfg-search">
                                <FiSearch size={12} />
                                <input type="text" value={search} placeholder={t('common:sess.searchPlaceholder')}
                                    onChange={e => setSearch(e.target.value)} />
                            </div>

                            <div className="sscfg-params">
                                {params.slice(0, 200).map(p => (
                                    <div className="sscfg-param" key={`${p.group}:${p.name}`}>
                                        <span className={`sscfg-tag ${p.group.toLowerCase()}`}>{p.group}</span>
                                        <code className="sscfg-pname" title={p.description || ''}>{p.name}</code>
                                        <span className="sscfg-pval" title={p.value || ''}>{p.value || '-'}</span>
                                        <button className="sscfg-mini" title={t('common:sess.override')}
                                            onClick={async () => {
                                                // 공통 입력 대화상자 — 지금 값(재정의가 있으면 그것)을 미리 채운다
                                                const v = await prompt({
                                                    title: t('common:sess.override'),
                                                    message: t('common:sess.valueFor', { name: p.name }),
                                                    defaultValue: overrides[p.name] ?? (p.value || ''),
                                                });
                                                if (v != null) setOverride(p.name, v);
                                            }}>
                                            <FiPlus size={11} />
                                        </button>
                                    </div>
                                ))}
                                {params.length === 0 && <div className="sscfg-empty">{t('common:sess.noParams')}</div>}
                                {params.length > 200 && <div className="sscfg-empty">{t('common:sess.andMore', { n: params.length - 200 })}</div>}
                            </div>
                        </>
                    )}

                    {error && <div className="sscfg-error">{error}</div>}
                    {notice && <div className="sscfg-notice">{notice}</div>}
                </div>

                <div className="sscfg-footer app-modal-footer">
                    <button className="app-btn" onClick={async () => {
                        await axios.delete(SS_API, { data: { connectionId } });
                        load();
                        setNotice(t('common:sess.resetDone'));
                    }} disabled={busy || !connectionId}>{t('common:sess.reset')}</button>
                    <div className="sscfg-spacer" />
                    <button className="app-btn app-btn-primary" onClick={onClose}>{t('common:sess.close')}</button>
                </div>
        </AppModal>
    );
};

export default SessionSettingsModal;
